import { useMemo, useState } from 'react';
import { useOpsPage } from '../hooks/useDailyInstallations';
import OpsRecordList, { ukDate } from '../components/OpsRecordList';
import {
  BRAND, BarRow, DayChart, Note, PageHeader, Panel, States, Stat,
  dec, num, pct, selectClass, thisMonth,
} from '../components/OpsPageKit';

// Whereabouts Master — the NEO2 rebuild.
//
// Where the field force actually was, day by day. The figure worth reading is
// installs booked per engineer-day on installations: it says whether the
// planning matched the people who were available to do the work.
const STATUS_COLOURS = {
  INSTALLATIONS: BRAND,
  SUPPORT: '#7C3AED',
  'CIVIL/PREPULL': '#0891B2',
  'CIVILS/PRE PULL': '#0891B2',
  'ANNUAL LEAVE': '#16A34A',
  SICK: '#DC2626',
  REMEDIATIONS: '#D97706',
  BUDDYING: '#DB2777',
  'CROSS TRAINING': '#65A30D',
  'EXTERNAL TRAINING': '#A16207',
  'NOT ACTIONED': '#9CA3AF',
};
const colourFor = (s) => STATUS_COLOURS[String(s).toUpperCase()] || '#9CA3AF';

function WhereaboutsMaster() {
  const [month, setMonth] = useState(thisMonth());
  const [region, setRegion] = useState('');

  const filters = useMemo(() => ({ month, region }), [month, region]);
  const {
    data, isLoading, isFetching, refetch, isError,
  } = useOpsPage('whereabouts-master', filters);

  const t = data?.totals || {};
  const regions = (data?.by_region || []).map((r) => r.region);
  const maxStatus = Math.max(1, ...(data?.statuses || []).map((s) => s.n));

  return (
    <div className="p-6">
      <PageHeader
        title="Whereabouts Master"
        subtitle="How the field force spent its days, and whether the work matched the people."
        month={month}
        onMonth={setMonth}
        onRefresh={refetch}
        fetching={isFetching}
      >
        <select value={region} onChange={(e) => setRegion(e.target.value)} className={selectClass}>
          <option value="">All areas</option>
          {regions.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
      </PageHeader>

      <States isLoading={isLoading} isError={isError} what="whereabouts" />

      {data && (
        <>
          <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-5">
            <Stat label="Engineer days" value={num(t.status_rows)} sub={`across ${t.days} days`} accent />
            <Stat label="On installations" value={num(t.installation_days)} sub="engineer days" />
            <Stat label="Installs booked" value={num(t.installs_booked)} sub="same window" />
            <Stat
              label="Per engineer day"
              value={dec(t.installs_per_engineer_day, 2)}
              sub="booked ÷ installation days"
              accent
            />
            <Stat
              label="Sick days"
              value={num(t.sick_days)}
              sub={`${num(t.sick_days_x4)} at the report's ×4`}
              tone={t.sick_days ? '#DC2626' : undefined}
            />
          </div>

          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            <Panel title="Day by day">
              <DayChart
                rows={data.by_day}
                series={[
                  { key: 'installations', label: 'Installations', colour: BRAND },
                  { key: 'support', label: 'Support', colour: '#7C3AED' },
                  { key: 'annual_leave', label: 'Annual leave', colour: '#16A34A' },
                  { key: 'sick', label: 'Sick', colour: '#DC2626' },
                ]}
                height={140}
              />
            </Panel>

            <Panel title="Status mix">
              <div className="py-1">
                {data.statuses.map((s) => (
                  <BarRow
                    key={s.status}
                    label={s.status}
                    value={s.n}
                    total={maxStatus}
                    sub={`${pct(s.pct)} · ${s.engineers} eng`}
                    colour={colourFor(s.status)}
                  />
                ))}
              </div>
            </Panel>
          </div>

          {data.by_region.length > 1 ? (
            <div className="mt-4">
              <Panel title="By region">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                      <tr>
                        <th className="px-3 py-2">Region</th>
                        <th className="px-3 py-2 text-right">Engineer days</th>
                        <th className="px-3 py-2 text-right">On installations</th>
                        <th className="px-3 py-2 text-right">Share on installations</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {data.by_region.map((r) => (
                        <tr key={r.region} className="hover:bg-gray-50">
                          <td className="px-3 py-1.5 text-gray-800">{r.region}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums text-gray-700">{num(r.n)}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums text-gray-700">{num(r.installations)}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums text-gray-500">
                            {pct(r.n ? r.installations / r.n : 0)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Panel>
            </div>
          ) : null}

          <Note>
            “Not Actioned” in the original counts rows whose <em>region</em> is Operations rather
            than a status of its own — that quirk is kept here so the two agree. The ×4 on sick
            days is the report's own cost multiplier, not a measured figure.
          </Note>
        </>
      )}

      <OpsRecordList
        page="whereabouts-master"
        title="Whereabouts"
        searchPlaceholder="Name, email, status, area…"
        columns={[
          { head: 'Date', className: 'whitespace-nowrap tabular-nums text-gray-800', cell: (r) => ukDate(r.whereabouts_date) },
          { head: 'Engineer', className: 'text-gray-800', cell: (r) => r.engineer || r.engineer_email || '—' },
          { head: 'Status', cell: (r) => r.status || '—' },
          { head: 'Region', cell: (r) => r.region_contract || '—' },
          { head: 'Area', cell: (r) => r.region || '—' },
          { head: 'Clocked in', className: 'whitespace-nowrap tabular-nums text-gray-600', cell: (r) => { const d = r.time_clocked_in ? new Date(r.time_clocked_in) : null; return d && !Number.isNaN(d.getTime()) ? d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London' }) : '—'; } },
          { head: 'Self-certified', cell: (r) => (String(r.self_certified).toLowerCase() === 'true' ? 'Yes' : 'No') },
        ]}
      />
    </div>
  );
}

export default WhereaboutsMaster;
